import { esErrorDeRed, mensajeDeError } from '@/core/red/conectividad';

/**
 * Lo que dice el servicio de cuentas, dicho en castellano y para una persona.
 *
 * ## Por qué hace falta
 *
 * `mensajeDeError` deja pasar tal cual lo que contesta el servidor, y para las
 * funciones de este proyecto está bien: son mensajes escritos acá, en
 * castellano, y explican qué corregir —«el reparto suma 90 % y debe sumar
 * 100 %»—.
 *
 * El servicio de cuentas de Supabase no. Contesta en inglés y con la jerga de
 * la biblioteca: alguien que se equivocó de contraseña leyó **«Invalid login
 * credentials»** en rojo, en un teléfono, en el campo. No dice qué hacer y ni
 * siquiera dice en qué idioma está el problema.
 *
 * ## Cómo traduce
 *
 * Por el texto y no por un código, porque `auth-js` no expone uno estable para
 * la mayoría de estos casos. Es frágil a propósito en una sola dirección: si
 * mañana cambian una frase, se cae al mensaje genérico —que sigue siendo
 * castellano— en vez de mostrar inglés.
 *
 * Y nunca se dice más de lo que corresponde. Ante una contraseña equivocada no
 * se aclara si el correo existe o no: eso le confirmaría a cualquiera quién
 * tiene cuenta.
 */

/** Cada patrón con lo que hay que decir. El primero que coincide gana. */
const TRADUCCIONES: [RegExp, string][] = [
  [
    /invalid login credentials/i,
    // A propósito no distingue «no existe» de «contraseña equivocada»: decirlo
    // sería confirmarle a cualquiera qué correos tienen cuenta.
    'El correo o la contraseña no coinciden. Revisalos y probá de nuevo.',
  ],
  [
    /email not confirmed/i,
    'Todavía no confirmaste tu correo. Buscá el mensaje que te enviamos y abrí el enlace.',
  ],
  [
    /user already registered|already been registered/i,
    'Ya existe una cuenta con ese correo. Entrá con tu contraseña, o pedí una nueva si la olvidaste.',
  ],
  [
    /password should be at least (\d+)/i,
    'La contraseña es demasiado corta: tiene que tener al menos 6 caracteres.',
  ],
  [/unable to validate email address|invalid format/i, 'Ese correo no parece estar bien escrito.'],
  [/for security purposes.*?(\d+) seconds/i, 'Esperá unos segundos antes de volver a intentar.'],
  [
    /email rate limit exceeded|over_email_send_rate_limit/i,
    'Se enviaron demasiados correos seguidos. Esperá unos minutos y probá otra vez.',
  ],
  [
    /signups not allowed/i,
    'Por ahora no se pueden crear cuentas nuevas. Escribinos y te damos acceso.',
  ],
  [
    /token has expired or is invalid|invalid or has expired/i,
    'Ese enlace ya venció. Pedí uno nuevo.',
  ],
  [
    /new password should be different/i,
    'La contraseña nueva tiene que ser distinta de la anterior.',
  ],
  [/user not found/i, 'No encontramos una cuenta con ese correo.'],
];

/**
 * El error del servicio de cuentas, para mostrar.
 *
 * Los fallos de red siguen el camino de siempre: los traduce `mensajeDeError`,
 * que es el que sabe distinguir «no hay señal» de «el servidor dijo que no».
 */
export function mensajeDeAuth(e: unknown): string {
  if (esErrorDeRed(e)) {
    // El mensaje general de red dice «lo que cargues se guarda en el teléfono»,
    // y en la pantalla de ingreso eso es mentira: no se está cargando nada y no
    // se entró. Quien lo leyó en modo avión entendió, razonablemente, que la
    // aplicación debería dejarlo pasar igual.
    //
    // Lo que hay que decir acá es por qué NO puede: verificar una contraseña es
    // preguntarle al servidor. En el teléfono no hay contra qué compararla.
    return mensajeDeError(
      e,
      'Para entrar hace falta señal: el teléfono no puede verificar una contraseña por su cuenta.',
    );
  }

  const crudo = (e as { message?: string })?.message ?? '';
  for (const [patron, texto] of TRADUCCIONES) {
    if (patron.test(crudo)) return texto;
  }

  // Nada coincidió. Antes que mostrar inglés, se dice algo cierto y en
  // castellano: quien lo lea no va a poder actuar sobre la frase original de
  // todos modos.
  return 'No pudimos completar la operación. Probá de nuevo en un momento.';
}
